import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/store/cart";
import { useConfig } from "@/context/ConfigContext";
import { CheckCircle2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [attempts, setAttempts] = useState(0);
  const [order, setOrder] = useState(null);
  const clearCart = useCart((s) => s.clear);
  const config = useConfig();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async (n = 0) => {
      if (n >= 8) {
        if (!cancelled) setStatus("failed");
        return;
      }
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setAttempts(n + 1);
        setOrder(r.data);
        if (r.data.payment_status === "paid") {
          clearCart();
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clearCart]);

  const isPickup = order?.shipping_method === "pickup_invercargill";
  const contact = config?.contact_email || "shootagun@hotmail.com";

  return (
    <div data-testid="checkout-success" className="max-w-2xl mx-auto px-6 py-32 text-center">
      {status === "checking" && (
        <>
          <p className="smallcaps mb-4">Processing</p>
          <h1 className="font-display text-4xl text-[#212121] mb-4">Confirming your order…</h1>
          <p className="text-[#757575]">This usually takes a few seconds. (Attempt {attempts}/8)</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-[#B87333] font-bold mb-6" size={64} />
          <p className="smallcaps mb-4">Confirmed</p>
          <h1 className="font-display text-5xl text-[#212121] mb-4">Thank you.</h1>
          {isPickup ? (
            <>
              <p className="text-[#757575] mb-8">
                Your order is in the workshop queue. A confirmation email is on its way.
              </p>
              <div
                className="bg-[#B2DFDB] border border-[#B87333] p-6 mb-10 text-left"
                data-testid="pickup-notice-success"
              >
                <p className="smallcaps text-[#B87333] font-bold mb-3 text-center">⚑ One last step — local pickup</p>
                <p className="text-[#212121] mb-4 leading-relaxed">
                  Please email David to arrange a pickup time at the workshop in Invercargill:
                </p>
                <a
                  href={`mailto:${contact}?subject=Pickup arrangement for my order&body=Hi David, I just placed an order for local pickup. When would suit you for me to collect it?`}
                  className="btn-primary w-full justify-center"
                  data-testid="pickup-email-btn"
                >
                  Email {contact}
                </a>
              </div>
            </>
          ) : (
            <p className="text-[#757575] mb-10">
              Your order is in the workshop queue. You&apos;ll get a confirmation email shortly with shipping details.
            </p>
          )}
          <Link to="/shop" className="btn-primary">Keep browsing</Link>
        </>
      )}
      {status === "failed" && (
        <>
          <p className="smallcaps mb-4 text-[#D32F2F]">Something went wrong</p>
          <h1 className="font-display text-4xl text-[#212121] mb-4">We couldn&apos;t confirm payment.</h1>
          <p className="text-[#757575] mb-10">
            If you were charged, drop us a line — otherwise feel free to try again.
          </p>
          <Link to="/shop" className="btn-secondary">Back to shop</Link>
        </>
      )}
    </div>
  );
}
